// Authentication service – manages tokens, device ID and calls to the Auth API.

const AUTH_BASE = (process.env.REACT_APP_AUTH_URL ?? 'http://localhost:5000').replace(/\/$/, '');
const SUBSCRIPTION_KEY = process.env.REACT_APP_SUBSCRIPTION_KEY ?? '';

/** Common headers attached to every auth API request. */
function commonHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  if (SUBSCRIPTION_KEY) headers['Ocp-Apim-Subscription-Key'] = SUBSCRIPTION_KEY;
  return headers;
}

const KEYS = {
  deviceId: 'auth_device_id',
  token: 'auth_token',
  refreshToken: 'auth_refresh_token',
  tokenExpiry: 'auth_token_expiry',
  username: 'auth_username',
  emailConfirmPending: 'auth_email_confirm_pending',
} as const;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthResponse {
  token: string;
  refreshToken: string;
  tokenExpiry: string; // ISO-8601 date string
  username?: string;
}

// ── Device ID ─────────────────────────────────────────────────────────────────
// A stable GUID identifying this browser – created once and persisted in
// localStorage so every register / login / refresh call sends the same value.

function generateGuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getDeviceId(): string {
  let id = localStorage.getItem(KEYS.deviceId);
  if (!id) {
    id = generateGuid();
    localStorage.setItem(KEYS.deviceId, id);
  }
  return id;
}

// ── Token storage helpers ─────────────────────────────────────────────────────

function saveAuthResponse(data: AuthResponse, username?: string): void {
  localStorage.setItem(KEYS.token, data.token);
  localStorage.setItem(KEYS.refreshToken, data.refreshToken);
  localStorage.setItem(KEYS.tokenExpiry, data.tokenExpiry);
  if (username) {
    localStorage.setItem(KEYS.username, username);
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(KEYS.token);
}

export function getStoredUsername(): string | null {
  return localStorage.getItem(KEYS.username);
}

export function getStoredEmailConfirmPending(): boolean {
  return localStorage.getItem(KEYS.emailConfirmPending) === '1';
}

export function setStoredEmailConfirmPending(pending: boolean): void {
  if (pending) {
    localStorage.setItem(KEYS.emailConfirmPending, '1');
  } else {
    localStorage.removeItem(KEYS.emailConfirmPending);
  }
}

function getStoredRefreshToken(): string | null {
  return localStorage.getItem(KEYS.refreshToken);
}

export function isTokenExpired(): boolean {
  const expiry = localStorage.getItem(KEYS.tokenExpiry);
  if (!expiry) return true;
  // Add a 30-second buffer so we refresh slightly before hard expiry
  return new Date(expiry).getTime() - 30_000 <= Date.now();
}

export function clearAuth(): void {
  localStorage.removeItem(KEYS.token);
  localStorage.removeItem(KEYS.refreshToken);
  localStorage.removeItem(KEYS.tokenExpiry);
  localStorage.removeItem(KEYS.username);
  localStorage.removeItem(KEYS.emailConfirmPending);
}

export function isAuthenticated(): boolean {
  return !!getStoredToken();
}

// ── Error parsing ─────────────────────────────────────────────────────────────

async function parseError(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const json = JSON.parse(text);
    const firstErrorMessage = Array.isArray(json.errors) && json.errors.length > 0
      ? (json.errors[0]?.message ?? json.errors[0]?.code)
      : undefined;
    return (
      firstErrorMessage ??
      json.message ??
      json.title ??
      json.error ??
      `Request failed (${res.status})`
    );
  } catch {
    return text || `Request failed (${res.status})`;
  }
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function register(
  username: string,
  email: string,
  password: string,
): Promise<AuthResponse> {
  const res = await fetch(`${AUTH_BASE}/accounts/register`, {
    method: 'POST',
    headers: commonHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ username, email, password, deviceId: getDeviceId() }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const data: AuthResponse = await res.json();
  saveAuthResponse(data, data.username ?? username);
  // Newly-registered accounts always need email confirmation.
  setStoredEmailConfirmPending(true);
  return data;
}

export async function login(usernameOrEmail: string, password: string): Promise<AuthResponse> {
  const res = await fetch(`${AUTH_BASE}/accounts/login`, {
    method: 'POST',
    headers: commonHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ username: usernameOrEmail, password, deviceId: getDeviceId() }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const data: AuthResponse = await res.json();
  saveAuthResponse(data, data.username ?? usernameOrEmail);
  return data;
}

/**
 * Fire-and-forget ping to wake a cold-started auth API (Fly.io machine scaled
 * to zero). Errors are swallowed — this is best-effort warmup, not a
 * dependency of anything else.
 */
export function wakeAuthApi(): void {
  fetch(`${AUTH_BASE}/index`, { headers: commonHeaders() }).catch(() => {});
}

export async function forgotPassword(email: string): Promise<void> {
  const res = await fetch(`${AUTH_BASE}/accounts/forgot-password`, {
    method: 'POST',
    headers: commonHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await parseError(res));
}

export async function resetPassword(
  email: string,
  code: string,
  password: string,
): Promise<void> {
  const res = await fetch(`${AUTH_BASE}/accounts/reset-password`, {
    method: 'POST',
    headers: commonHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ email, code, password }),
  });
  if (!res.ok) throw new Error(await parseError(res));
}

// Friendly messages for known API error codes (returned in the `errors[]` array)
const FRIENDLY_ERROR_MESSAGES: Record<string, string> = {
  InvalidRegistrationConfirmation: 'That code is incorrect or has expired. Please check the code and try again.',
};

function pickErrorMessage(json: any): string | undefined {
  if (Array.isArray(json?.errors) && json.errors.length > 0) {
    const first = json.errors[0];
    const code: string | undefined = first?.code;
    const message: string | undefined = first?.message;
    if (message && message.trim()) return message;
    if (code && FRIENDLY_ERROR_MESSAGES[code]) return FRIENDLY_ERROR_MESSAGES[code];
    if (code) return code;
  }
  return json?.message || json?.title || json?.error;
}

export async function confirmAccount(username: string, code: string): Promise<void> {
  const url = `${AUTH_BASE}/accounts/confirm-account?username=${encodeURIComponent(username)}&code=${encodeURIComponent(code)}`;
  const res = await fetch(url, { method: 'POST', headers: commonHeaders() });
  const text = await res.text().catch(() => '');
  let json: any = null;
  if (text) {
    try { json = JSON.parse(text); } catch { /* non-JSON response */ }
  }
  // The endpoint can return 200 with an `errors` array — treat that as failure.
  const hasErrors = json && Array.isArray(json.errors) && json.errors.length > 0;
  if (!res.ok || hasErrors) {
    throw new Error(
      pickErrorMessage(json) ?? text ?? `Request failed (${res.status})`,
    );
  }
  setStoredEmailConfirmPending(false);
}

// ── Silent token refresh ──────────────────────────────────────────────────────
// A single in-flight promise is reused so concurrent requests don't each
// independently kick off a refresh call.

let pendingRefresh: Promise<AuthResponse> | null = null;

export function silentRefresh(): Promise<AuthResponse> {
  if (pendingRefresh) return pendingRefresh;

  const token = getStoredToken();
  const refreshToken = getStoredRefreshToken();

  if (!token || !refreshToken) {
    return Promise.reject(new Error('No credentials available for token refresh'));
  }

  pendingRefresh = fetch(`${AUTH_BASE}/accounts/refresh-token`, {
    method: 'POST',
    headers: commonHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      accessToken: token,
      refreshToken,
      deviceId: getDeviceId(),
    }),
  })
    .then(async (res) => {
      if (!res.ok) {
        clearAuth();
        throw new Error(await parseError(res));
      }
      const data: AuthResponse = await res.json();
      saveAuthResponse(data);
      return data;
    })
    .finally(() => {
      pendingRefresh = null;
    });

  return pendingRefresh;
}
